import { addToast, Select, SelectItem, Textarea } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import SendCallBottomBar from 'components/Dashboard/SendCall/SendCallBottomBar'
import LinearForm from 'components/Hero/LinearForm'
import ObjectInput from 'components/Hero/ObjectInput'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { $fetch } from 'utils/fetch'
import SMSTestButton from '../SMSTestButton'
import SMSRecipientsUpload from './SMSRecipientsUpload'

const CSVFormDefinition = {
  fields: [
    {
      name: 'file_id',
      component: SMSRecipientsUpload,
      uncontrolled: true,
    },
    {
      name: 'request_data',
      component: ObjectInput,
      uncontrolled: true,
      props: {
        name: 'request_data',
        label: 'Global Request Data (Optional)',
        description: 'Additional data to include with each SMS (applies to all recipients)',
      },
    },
    {
      name: 'message',
      component: Textarea,
      props: {
        label: 'Message Content Override (Optional)',
        placeholder: 'Enter your SMS message if you want to use a static message...',
        description: 'Does not generate a response from the pathway or prompt from the number, and instead sends the below message content',
        size: 'md',
        variant: 'bordered',
        radius: 'sm',
        minRows: 3,
        maxRows: 6,
      },
    },
  ],
}

export default function SMSBatchForm({ availableNumbers, isEnterprise }) {
  const navigate = useNavigate()

  const methods = useForm({
    defaultValues: {
      from_number: '',
      message: '',
      request_data: {},
      file_id: null,
      column_mapping: {},
      recipients_count: 0,
    },
  })

  const fileId = useWatch({ control: methods.control, name: 'file_id' })
  const recipientsCount = useWatch({ control: methods.control, name: 'recipients_count' })
  const selectedFromNumber = useWatch({ control: methods.control, name: 'from_number' })

  const selectedNumberData = availableNumbers.find(num => num.phone_number === selectedFromNumber)

  const sendBatchSMS = useMutation({
    mutationFn: async (data) => {
      if (!data.file_id) {
        throw new Error('Please upload a CSV file with recipients')
      }

      const payload = {
        file_id: data.file_id,
        global: {
          agent_number: data.from_number,
          agent_message: data.message,
          request_data: data.request_data,
        },
        column_mapping: data.column_mapping,
      }

      return await $fetch('/v1/sms/batch', {
        method: 'POST',
        body: payload,
      })
    },
    onSuccess: (response) => {
      const { successful = 0, failed = 0, recipients_processed = 0, status } = response

      if (status === 'completed') {
        // Completed CSV processing
        addToast({
          title: 'Batch SMS Sent',
          description: `Successfully sent to ${successful} recipients. ${failed > 0 ? `${failed} failed.` : ''}`,
          color: failed > 0 ? 'warning' : 'success',
        })
      }
      else {
        // CSV mode - processing started
        addToast({
          title: 'Batch SMS Started',
          description: `SMS batch processing has started for ${recipients_processed || recipientsCount} recipients`,
          color: 'success',
        })
      }

      methods.reset()

      // Navigate to SMS dashboard conversations tab with fromBatch flag to trigger refetch
      navigate('/dashboard/sms?tab=conversations&fromBatch=true')
    },
    onError: (error) => {
      addToast({
        title: 'Failed to send batch SMS',
        description: error.message || 'Unknown error occurred',
        color: 'danger',
      })
    },
  })

  const onSubmit = async (data) => {
    await sendBatchSMS.mutateAsync(data)
  }

  const handleSubmit = methods.handleSubmit(onSubmit)

  return (
    <div className="mx-auto w-full max-w-4xl p-6">
      {/* Header outside the card */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-semibold">Send Batch SMS</h2>
        <p className="text-gray-600">Upload a CSV file to send SMS messages to multiple recipients at once</p>
      </div>

      {/* White card container for the form */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-xs">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-6">
            {/* From Number Select with Test Button */}
            <div className="flex flex-col gap-y-3">
              <Select
                label="From Number"
                placeholder="Select a phone number"
                description="The phone number to send SMS from"
                size="md"
                variant="bordered"
                radius="sm"
                isRequired
                selectedKeys={selectedFromNumber ? [selectedFromNumber] : []}
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0]
                  methods.setValue('from_number', selectedKey)
                }}
              >
                {availableNumbers.map(number => (
                  <SelectItem key={number.phone_number} value={number.phone_number}>
                    {number.phone_number}
                  </SelectItem>
                ))}
              </Select>

              {/* Selected Number Configuration Info & Test Button */}
              {selectedFromNumber && selectedNumberData && (
                <div className={`
                  rounded-lg border border-gray-200 bg-gray-50 p-4
                `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="mb-2 text-sm font-medium text-gray-900">SMS Configuration</h4>
                      <div className={`
                        grid grid-cols-1 gap-3 text-xs
                        md:grid-cols-2
                      `}
                      >
                        {selectedNumberData.sms_config?.pathway_id && (
                          <div>
                            <span className="text-gray-500">Pathway ID:</span>
                            <span className="ml-1 font-mono text-gray-800">{selectedNumberData.sms_config.pathway_id}</span>
                          </div>
                        )}
                        {selectedNumberData.sms_config?.pathway_version && (
                          <div>
                            <span className="text-gray-500">Version:</span>
                            <span className="ml-1 font-mono text-gray-800">{selectedNumberData.sms_config.pathway_version}</span>
                          </div>
                        )}
                        {selectedNumberData.sms_config?.objective && (
                          <div className="col-span-full">
                            <span className="text-gray-500">Objective:</span>
                            <span className="ml-1 text-gray-800">
                              {selectedNumberData.sms_config.objective.length > 50
                                ? `${selectedNumberData.sms_config.objective.slice(0, 100)}...`
                                : selectedNumberData.sms_config.objective}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 shrink-0">
                      <SMSTestButton
                        selectedNumber={selectedFromNumber}
                        availableNumbers={availableNumbers}
                        disabled={!isEnterprise}
                        onSuccess={() => {}}
                        highlight={false}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CSV Upload Form Fields */}
            <div className="rounded-lg border p-4">
              <h3 className="mb-4 text-lg font-medium">CSV Upload</h3>
              <LinearForm
                formDefinition={CSVFormDefinition}
                className="flex flex-col gap-y-4"
              />
              {recipientsCount > 0 && (
                <div className="mt-3 text-sm text-gray-600">
                  Recipients found:
                  {' '}
                  <span className="font-medium text-primary">{recipientsCount}</span>
                </div>
              )}
            </div>

            {/* Send Button */}
            <SendCallBottomBar
              type="sms_batch"
              loading={sendBatchSMS.isPending}
              onSubmit={onSubmit}
              isDisabled={!isEnterprise || !fileId}
            />
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
